import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Mail, 
  Lock, 
  User, 
  Eye, 
  EyeOff, 
  LogIn,
  UserPlus,
  Stethoscope,
  Shield
} from 'lucide-react';
import { authAPI } from '../config/api';
import '../styles/Auth.css';

const Auth = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    terms: false
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'البريد الإلكتروني غير صالح';
    }

    if (!formData.password) {
      newErrors.password = 'كلمة المرور مطلوبة';
    } else if (formData.password.length < 6) {
      newErrors.password = 'كلمة المرور يجب أن تكون 6 أحرف على الأقل';
    }

    if (!isLogin) {
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'كلمة المرور غير متطابقة';
      }
      if (!formData.terms) {
        newErrors.terms = 'يجب الموافقة على الشروط والأحكام';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let response;

      if (isLogin) {
        // تسجيل الدخول
        response = await authAPI.login({
          email: formData.email,
          password: formData.password
        });
      } else {
        // التسجيل
        response = await authAPI.register({
          email: formData.email,
          password: formData.password,
          password_confirmation: formData.confirmPassword,
          is_admin: false
        });
      }

      // معالجة الاستجابة الناجحة
      if (response.success) {
        const { user, access_token } = response;
        
        // تخزين بيانات المستخدم
        localStorage.setItem('user', JSON.stringify({
          id: user.id,
          email: user.email,
          is_admin: user.is_admin,
          isAuthenticated: true
        }));
        
        localStorage.setItem('access_token', access_token);
        
        // رسالة نجاح
        alert(isLogin ? 'تم تسجيل الدخول بنجاح!' : 'تم إنشاء الحساب بنجاح!');
        
        // التوجيه والتحديث
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Auth error:', error);
      
      // معالجة أخطاء التحقق
      if (error.errors) {
        const serverErrors = {};
        Object.keys(error.errors).forEach(key => {
          if (key === 'email') serverErrors.email = error.errors[key][0];
          if (key === 'password') serverErrors.password = error.errors[key][0];
        });
        setErrors(serverErrors);
      } else {
        // رسالة خطأ عامة
        alert(error.message || 'حدث خطأ أثناء عملية المصادقة');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    setErrors({});
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      terms: false
    });
  };

  return (
    <div className="auth-page" dir="rtl">
      <div className="auth-container">
        {/* Header */}
        <div className="auth-header">
          <div className="auth-logo">
            <Stethoscope className="logo-icon" />
            <span className="logo-text">العيادة الطبية</span>
          </div>
          <h1 className="auth-title">
            {isLogin ? 'مرحباً بعودتك' : 'أنشئ حساب جديد'}
          </h1>
          <p className="auth-subtitle">
            {isLogin 
              ? 'سجل الدخول للوصول إلى حسابك والاستفادة من خدماتنا'
              : 'انضم إلى مجتمعنا الطبي واستفد من خدماتنا المتكاملة'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="auth-form">
          {/* Email Input */}
          <div className="form-group">
            <label className="form-label">
              <Mail className="label-icon" />
              <span>البريد الإلكتروني</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder="example@domain.com"
              disabled={loading}
              autoComplete="email"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          {/* Password Input */}
          <div className="form-group">
            <label className="form-label">
              <Lock className="label-icon" />
              <span>كلمة المرور</span>
            </label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="أدخل كلمة المرور"
                disabled={loading}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
                disabled={loading}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          {/* Confirm Password (for register) */}
          {!isLogin && (
            <div className="form-group">
              <label className="form-label">
                <Shield className="label-icon" />
                <span>تأكيد كلمة المرور</span>
              </label>
              <div className="password-input-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                  placeholder="أعد إدخال كلمة المرور"
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
              {errors.confirmPassword && (
                <span className="error-message">{errors.confirmPassword}</span>
              )}
            </div>
          )}

          {/* Terms (for register) */}
          {!isLogin && (
            <div className="form-group terms-group">
              <label className="terms-label">
                <input
                  type="checkbox"
                  name="terms"
                  checked={formData.terms}
                  onChange={handleInputChange}
                  className="terms-checkbox"
                  disabled={loading}
                />
                <span className="terms-text">
                  أوافق على <Link to="/terms" className="terms-link">الشروط والأحكام</Link> و
                  <Link to="/privacy" className="terms-link"> سياسة الخصوصية</Link>
                </span>
              </label>
              {errors.terms && <span className="error-message">{errors.terms}</span>}
            </div>
          )}

          {/* Forgot Password (for login) */}
          {isLogin && (
            <div className="forgot-password">
              <Link to="/auth" className="forgot-link">
                هل نسيت كلمة المرور؟
              </Link>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? (
              <span className="loading-spinner"></span>
            ) : (
              <>
                {isLogin ? (
                  <>
                    <LogIn className="button-icon" />
                    <span>تسجيل الدخول</span>
                  </>
                ) : (
                  <>
                    <UserPlus className="button-icon" />
                    <span>إنشاء حساب</span>
                  </>
                )}
              </>
            )}
          </button>
        </form>

        {/* Toggle Auth Mode */}
        <div className="auth-toggle">
          <p className="toggle-text">
            {isLogin ? 'ليس لديك حساب؟' : 'لديك حساب بالفعل؟'}
          </p>
          <button
            onClick={toggleAuthMode}
            className="toggle-button"
            disabled={loading}
          >
            {isLogin ? 'سجل الآن' : 'سجل الدخول'}
          </button>
        </div>

        {/* Features */}
        <div className="auth-features">
          <div className="feature">
            <Shield className="feature-icon" />
            <span>حماية وأمان عالي</span>
          </div>
          <div className="feature">
            <Stethoscope className="feature-icon" />
            <span>خدمات طبية متكاملة</span>
          </div>
          <div className="feature">
            <User className="feature-icon" />
            <span>دعم فني على مدار الساعة</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;